import { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Breadcrumb, Button, Form, Modal, Tag, Typography } from 'antd';
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    EnvironmentOutlined,
    SaveOutlined,
    WarningOutlined,
} from '@ant-design/icons';

import { ICity } from '../../../../interfaces';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import PageHeader from '../../../../components/PageHeader';

import { colInputs } from './GridStyle';
import useCityForm, { MODULES } from './useCityForm';
import FormStepsController from '../FormStepsController';
import RegisterStep from './Steps/RegisterStep';
import ConfigStep from './Steps/ConfigStep';
import FilesStep from './Steps/FilesStep';
import CustomStep from './Steps/CustomStep';

const { Text } = Typography;

interface IProps {
    action: 'Adicionar' | 'Editar';
    city?: ICity;
    onSubmit: (city: FormData) => Promise<void>;
    isLoadingCity?: boolean;
}

const STEP_NAMES = [
    'Cadastro',
    'Configurações',
    'Arquivos',
    'Customização',
];

export default function CityForm({ action, city, onSubmit, isLoadingCity }: IProps) {
    const navigate = useNavigate();
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        handleSubmit,
        handleSubmitForm,
        control,
        isSubmiting,
        errors,
        clearErrors,
        uf,
        cityName,
        cities,
        isLoadingCities,
        setValue,
        handleChangeLightLogoImage,
        handleChangeDarkLogoImage,
        handleChangeBackgroundImage,
        handleChangeLogoCountyImage,
        handleChangeTermsOfUseFile,
        handleChangeRaffleRulesFile,
        lightLogoImageUrl,
        darkLogoImageUrl,
        backgroundImageUrl,
        logoCountyImageUrl,
        termsOfUseFileName,
        raffleRulesFileName,
        hasTermsOfUse,
        needsToInformPointsConfigEffectiveDate,
        needsToInformTicketsConfigEffectiveDate,
        needsToInformTermsOfUseEffectiveDate,
        upcomingConfig,
        systems,
        currentStep,
        refIsEditing,
        refSteps,
        alreadyIncludedModules,
        hasNewModules,
        newModules,
        trigger,
        formHasInvoiceNFCModule,
        formHasInvoiceNFSModule,
        formHasRewardModule,
        formHasTaxesModule,
        formHasTaxesAutoModule,
        isConfirmAddNewModulesModalOpen,
        refForm,
        errorsLength,
        handleCloseConfirmAddNewModulesModal,
        handleOpenConfirmAddNewModulesModal,
        handleConfirmAddNewModules,
        handleClickRegisterStep,
        handleClickConfigStep,
        handleClickFilesStep,
        handleClickCustomizationStep,
        handleClickNextStep,
        handleClickPreviousStep,
        ACCEPTED_IMAGE_TYPES,
        ACCEPTED_FILE_TYPES,
        watch,
    } = useCityForm(onSubmit, city, isLoadingCity);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 10,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                        : '0 2px 10px rgba(0, 0, 0, 0.03)',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/municipios">Municípios</Link>,
                        },
                        {
                            title: `${action} Município`,
                        },
                    ]}
                />
            </div>

            <PageHeader
                icon={<EnvironmentOutlined />}
                iconBg="linear-gradient(135deg, #1677ff 0%, #13c2c2 100%)"
                title={`${action} Município${city ? ` - ${city.city} - ${city.uf}` : ''}`}
                tag={
                    <Tag
                        color="blue"
                        style={{ fontWeight: 600, borderRadius: 6, padding: '2px 8px' }}
                    >
                        Etapa {currentStep + 1} de 4: {STEP_NAMES[currentStep]}
                    </Tag>
                }
                subtitle="Configure os dados gerais, módulos ativos, divisores de pontos/bilhetes, envio de arquivos e personalização visual."
                actions={
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/municipios')}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            borderRadius: 8,
                            fontWeight: 500,
                        }}
                    >
                        Voltar para Municípios
                    </Button>
                }
            />

            <FormStepsController
                refSteps={refSteps}
                currentStep={currentStep}
                errors={errors}
                items={[
                    {
                        title: 'Cadastro',
                        onClick: handleClickRegisterStep,
                        fields: ['name', 'cnpj', 'city', 'uf'],
                    },
                    {
                        title: 'Configurações',
                        onClick: handleClickConfigStep,
                        fields: [
                            'modules',
                            'nfc_points_divider',
                            'nfc_max_amount_points',
                            'nfc_max_month_quantity_by_cnpj_points',
                            'nfs_points_divider',
                            'nfs_max_amount_points',
                            'nfs_max_month_quantity_by_cnpj_points',
                            'taxe_points_divider',
                            'taxe_max_amount_points',
                            'nfc_tickets_divider',
                            'nfc_max_amount_tickets',
                            'nfc_max_month_quantity_by_cnpj_tickets',
                            'nfs_tickets_divider',
                            'nfs_max_amount_tickets',
                            'nfs_max_month_quantity_by_cnpj_tickets',
                            'taxe_tickets_divider',
                            'taxe_max_amount_tickets',
                            'points_config_description',
                            'points_config_effective_date',
                            'tickets_config_description',
                            'tickets_config_effective_date',
                            'system_id',
                            'time_expire_points',
                            'time_expire_vouchers',
                        ],
                    },
                    {
                        title: 'Arquivos',
                        onClick: handleClickFilesStep,
                        fields: [
                            'logo_dark',
                            'logo_light',
                            'img_background',
                            'terms_of_use',
                            'terms_of_use_effective_date',
                        ],
                    },
                    {
                        title: 'Customização',
                        onClick: handleClickCustomizationStep,
                        fields: ['primary_color'],
                    },
                ]}
            />

            <Form
                ref={refForm}
                onFinish={handleSubmit(handleSubmitForm)}
                autoComplete="off"
                layout="vertical"
            >
                <div
                    style={{
                        background: isDark ? '#141414' : '#ffffff',
                        borderRadius: 10,
                        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                        boxShadow: isDark
                            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                            : '0 2px 10px rgba(0, 0, 0, 0.03)',
                        padding: '24px',
                    }}
                >
                    {currentStep === 0 && (
                        <RegisterStep
                            control={control}
                            errors={errors}
                            colInputs={colInputs}
                            cities={cities}
                            setValue={setValue}
                            selectedCity={{ uf, city: cityName }}
                            isLoadingCities={isLoadingCities}
                            clearErrors={clearErrors}
                            isEditing={refIsEditing.current}
                        />
                    )}

                    {currentStep === 1 && (
                        <ConfigStep
                            control={control}
                            errors={errors}
                            watch={watch}
                            setValue={setValue}
                            formHasInvoiceNFCModule={formHasInvoiceNFCModule}
                            formHasInvoiceNFSModule={formHasInvoiceNFSModule}
                            formHasRewardModule={formHasRewardModule}
                            formHasTaxesModule={formHasTaxesModule}
                            formHasTaxesAutoModule={formHasTaxesAutoModule}
                            alreadyIncludedModules={alreadyIncludedModules}
                            needsToInformPointsConfigEffectiveDate={
                                needsToInformPointsConfigEffectiveDate
                            }
                            needsToInformTicketsConfigEffectiveDate={
                                needsToInformTicketsConfigEffectiveDate
                            }
                            upcomingConfig={upcomingConfig}
                            systems={systems}
                            city={city}
                        />
                    )}

                    {currentStep === 2 && (
                        <FilesStep
                            control={control}
                            errors={errors}
                            city={city}
                            colInputs={colInputs}
                            lightLogoImageUrl={lightLogoImageUrl}
                            darkLogoImageUrl={darkLogoImageUrl}
                            backgroundImageUrl={backgroundImageUrl}
                            logoCountyImageUrl={logoCountyImageUrl}
                            termsOfUseFileName={termsOfUseFileName}
                            raffleRulesFileName={raffleRulesFileName}
                            hasRaffleRules={city?.raffle_rules_file !== null}
                            uploadedRaffleRulesFile={city?.raffle_rules_file}
                            hasTermsOfUse={hasTermsOfUse}
                            onChangeLightLogoImage={handleChangeLightLogoImage}
                            onChangeDarkLogoImage={handleChangeDarkLogoImage}
                            onChangeBackgroundImage={handleChangeBackgroundImage}
                            onChangeLogoCountyImage={handleChangeLogoCountyImage}
                            onChangeTermsOfUseFile={handleChangeTermsOfUseFile}
                            onChangeRaffleRulesFile={handleChangeRaffleRulesFile}
                            acceptedImageTypes={ACCEPTED_IMAGE_TYPES}
                            acceptedFileTypes={ACCEPTED_FILE_TYPES}
                            needsToInformTermsOfUseEffectiveDate={
                                needsToInformTermsOfUseEffectiveDate
                            }
                            upcomingTerms={upcomingConfig?.terms_of_use}
                        />
                    )}

                    {currentStep === 3 && (
                        <CustomStep
                            control={control}
                            errors={errors}
                            city={city}
                        />
                    )}

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginTop: 28,
                            paddingTop: 20,
                            borderTop: `1px solid ${isDark ? '#262626' : '#f0f0f0'}`,
                        }}
                    >
                        <Button
                            type="default"
                            htmlType="button"
                            icon={<ArrowLeftOutlined />}
                            disabled={isSubmiting || currentStep === 0}
                            loading={isSubmiting}
                            onClick={handleClickPreviousStep}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                borderRadius: 8,
                                fontWeight: 500,
                            }}
                        >
                            Anterior
                        </Button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {hasNewModules ? (
                                <Button
                                    type="primary"
                                    htmlType="button"
                                    icon={<SaveOutlined />}
                                    disabled={isSubmiting || errorsLength > 0}
                                    loading={isSubmiting}
                                    onClick={async () => {
                                        const passed = await trigger(undefined, {
                                            shouldFocus: false,
                                        });

                                        if (passed) {
                                            handleOpenConfirmAddNewModulesModal();
                                        }
                                    }}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        borderRadius: 8,
                                        fontWeight: 600,
                                        backgroundColor: '#1677ff',
                                    }}
                                >
                                    Salvar Município
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SaveOutlined />}
                                    disabled={isSubmiting || errorsLength > 0}
                                    loading={isSubmiting}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        borderRadius: 8,
                                        fontWeight: 600,
                                        backgroundColor: '#1677ff',
                                    }}
                                >
                                    Salvar Município
                                </Button>
                            )}

                            {currentStep < 3 && (
                                <Button
                                    type="default"
                                    htmlType="button"
                                    disabled={isSubmiting}
                                    loading={isSubmiting}
                                    onClick={handleClickNextStep}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        borderRadius: 8,
                                        fontWeight: 500,
                                    }}
                                >
                                    <span>Próximo</span>
                                    <ArrowRightOutlined />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </Form>

            <Modal
                open={isConfirmAddNewModulesModalOpen}
                onOk={handleConfirmAddNewModules}
                okButtonProps={{
                    disabled: isSubmiting,
                    loading: isSubmiting,
                    danger: false,
                }}
                cancelButtonProps={{
                    disabled: isSubmiting,
                }}
                okText="Confirmar Inclusão"
                cancelText="Cancelar"
                onCancel={handleCloseConfirmAddNewModulesModal}
                title="Confirmação de Novos Módulos"
                centered
            >
                <div style={{ padding: '12px 0' }}>
                    <p style={{ margin: 0, fontSize: 14 }}>
                        Deseja realmente adicionar os seguintes módulos ao Município?
                    </p>
                    <ul style={{ margin: '12px 0', paddingLeft: 20 }}>
                        {MODULES.filter((module) => newModules.includes(module.name)).map(
                            (module) => (
                                <li
                                    key={`modal-confirm-${module.name}`}
                                    style={{ fontSize: 13.5, fontWeight: 500, marginBottom: 4 }}
                                >
                                    {module.label}
                                </li>
                            )
                        )}
                    </ul>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 14px',
                            background: isDark ? 'rgba(255, 77, 79, 0.1)' : '#fff2f0',
                            border: `1px solid ${isDark ? 'rgba(255, 77, 79, 0.3)' : '#ffccc7'}`,
                            borderRadius: 6,
                            marginTop: 12,
                        }}
                    >
                        <WarningOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
                        <Text style={{ color: '#ff4d4f', fontSize: 12.5 }}>
                            Atenção: uma vez adicionado, o módulo <strong>NÃO</strong> poderá ser
                            removido!
                        </Text>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
